// src/pages/ProfilePage.jsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext'; // Use your Auth Context
import { auth } from '../config/firebase'; // Use your Firebase config path
import { sendPasswordResetEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, AlertCircle } from 'lucide-react';

// Shadcn UI Imports
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input"; // Keep for potential future edits
import { Label } from "@/components/ui/label";
import { toast } from "sonner"; // For feedback messages
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

function ProfilePage() {
  const { currentUser, currentUserProfile, loading } = useAuth(); // No need for loading state here, ProtectedRoute handles it
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const navigate = useNavigate();

  // This component assumes ProtectedRoute has already ensured currentUser and currentUserProfile exist and are approved.
  // If accessed directly somehow without protection, it might error.

  const handlePasswordReset = async () => {
    if (!currentUser || !currentUser.email) {
      toast.error("Error", { description: "Could not find user email to send reset link." });
      return;
    }

    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      toast.success("Password Reset Email Sent", {
        description: `An email has been sent to ${currentUser.email} with instructions. Please check your inbox (and spam folder).`,
        duration: 10000, // Keep message longer
      });
    } catch (error) {
      console.error("Password Reset Error:", error);
      toast.error("Password Reset Failed", {
        description: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleVerifyCurrentPassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }

    setIsResettingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      setIsVerified(true);
      setPasswordError(null);
    } catch (error) {
      console.error('Error verifying current password:', error);
      setPasswordError('Current password is incorrect');
      setIsVerified(false);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (!isVerified) {
      setPasswordError('Please verify your current password first');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    setIsResettingPassword(true);
    try {
      await updatePassword(currentUser, newPassword);
      toast.success('Password Updated', {
        description: 'Your password has been updated successfully.',
        duration: 5000,
      });
      // Reset all password fields and verification state
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsVerified(false);
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordError('Failed to update password. Please try again.');
      // Reset verification if the error is due to authentication
      if (error.code === 'auth/requires-recent-login') {
        setIsVerified(false);
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || !currentUserProfile) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen text-center p-4">
        <h2 className="text-xl font-semibold mb-2">Profile Not Loaded</h2>
        <p className="text-muted-foreground mb-4">Your profile data could not be loaded. Please try again later or contact support.</p>
        <Button onClick={() => navigate(0)} variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry Loading Profile
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Pending Status Alert */}
      {currentUserProfile.approvalStatus === 'pending' && (
        <Alert className="mb-6 bg-[#FFB800] border-[#FFB800] text-black">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Account Pending Approval</AlertTitle>
          <AlertDescription>
            Your account is currently pending admin approval. You will be notified once your account is approved.
          </AlertDescription>
        </Alert>
      )}

      {/* Rejected Status Alert */}
      {currentUserProfile.approvalStatus === 'rejected' && (
        <Alert className="mb-6 bg-destructive/10 border-destructive text-destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            Your access request has been rejected. Please contact support if you believe this is an error.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6">
        {/* Profile Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Profile Information</CardTitle>
            <CardDescription>
              Your personal account details and status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={currentUserProfile.displayName || `${currentUserProfile.firstName} ${currentUserProfile.lastName}`.trim() || 'N/A'}
                  readOnly
                  className="bg-background/50 border-border"
                />
                <p className="text-xs text-muted-foreground">Your full name as displayed to others</p>
              </div>

              {/* First Name */}
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={currentUserProfile.firstName || 'N/A'}
                  readOnly
                  className="bg-background/50 border-border"
                />
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={currentUserProfile.lastName || 'N/A'}
                  readOnly
                  className="bg-background/50 border-border"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={currentUserProfile.email || currentUser.email || 'N/A'}
                  readOnly
                  className="bg-background/50 border-border"
                />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>

              {/* Account Status */}
              <div className="space-y-2">
                <Label>Account Status</Label>
                <Input
                  value={currentUserProfile.approvalStatus || 'Unknown'}
                  readOnly
                  className={cn(
                    "bg-background/50 border-border capitalize",
                    currentUserProfile.approvalStatus === 'approved' && "text-green-500",
                    currentUserProfile.approvalStatus === 'pending' && "text-yellow-500",
                    currentUserProfile.approvalStatus === 'rejected' && "text-destructive"
                  )}
                />
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label>Role</Label>
                <Input
                  value={currentUserProfile.role || 'User'}
                  readOnly
                  className="bg-background/50 border-border capitalize"
                />
              </div>

              {/* Auth Provider */}
              <div className="space-y-2">
                <Label>Authentication Method</Label>
                <Input
                  value={currentUserProfile.authProvider || 'N/A'}
                  readOnly
                  className="bg-background/50 border-border capitalize"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password Reset Card - Only show for non-Google users */}
        {currentUser.providerData[0]?.providerId !== 'google.com' && (
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>
                Update your account password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={isVerified ? handlePasswordChange : handleVerifyCurrentPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    placeholder="Enter your current password"
                  />
                </div>

                {isVerified && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        placeholder="Enter your new password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        placeholder="Confirm your new password"
                      />
                    </div>
                  </>
                )}

                {passwordError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" disabled={isResettingPassword}>
                  {isResettingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isVerified ? 'Update Password' : 'Verify Current Password'}
                </Button>

                {isVerified && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsVerified(false);
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;